import FAQItems from "../components/FAQItems";
import ScreenshotFrame from "../../components/ScreenshotFrame";

// Every factual claim maps to the source map in
// docs/ci-research-chatgpt-ads-custom-audiences/FINDINGS-2026-10-17.md
// (OpenAI help + developer docs, plus first-hand Ads Manager captures).

const TEXT = "#EDEDED";
const TEXT_DIM = "#a0a0a0";
const TEAL = "#4FCDC4";
const AMBER = "#E0B057";
const CARD_BORDER = "rgba(255,255,255,0.04)";
const CONVERTER = "/tools/google-ads-to-chatgpt-ads-converter";
const MINIMUM = "/blog/chatgpt-ads-custom-audiences-minimum";

function H2({ id, children }) {
  return (
    <h2
      id={id}
      style={{
        fontFamily: "var(--display)",
        fontWeight: 700,
        fontSize: "clamp(22px, 3vw, 28px)",
        color: TEXT,
        margin: "48px 0 16px",
        letterSpacing: "-0.01em",
        lineHeight: 1.2,
      }}
    >
      {children}
    </h2>
  );
}

function H3({ children }) {
  return (
    <h3
      style={{
        fontFamily: "var(--display)",
        fontWeight: 600,
        fontSize: 19,
        color: TEXT,
        margin: "28px 0 12px",
        lineHeight: 1.3,
      }}
    >
      {children}
    </h3>
  );
}

function P({ children }) {
  return (
    <p style={{ fontSize: 16, lineHeight: 1.75, color: TEXT_DIM, margin: "0 0 18px" }}>
      {children}
    </p>
  );
}

function Li({ children }) {
  return (
    <li style={{ fontSize: 16, lineHeight: 1.7, color: TEXT_DIM, marginBottom: 10 }}>
      {children}
    </li>
  );
}

function Strong({ children }) {
  return <strong style={{ color: TEXT, fontWeight: 600 }}>{children}</strong>;
}

function A({ href, children }) {
  return (
    <a
      href={href}
      style={{ color: TEAL, textDecoration: "none", borderBottom: `1px solid ${TEAL}44` }}
      {...(href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      {children}
    </a>
  );
}

function Code({ children }) {
  return (
    <code
      style={{
        background: "rgba(255,255,255,0.06)",
        padding: "2px 6px",
        borderRadius: 4,
        fontFamily: "var(--mono)",
        fontSize: 13,
        color: TEXT,
      }}
    >
      {children}
    </code>
  );
}

function PullQuote({ children }) {
  return (
    <blockquote
      style={{
        margin: "32px 0",
        padding: "20px 24px",
        borderLeft: `3px solid ${TEAL}`,
        background: "rgba(79,205,196,0.05)",
        borderRadius: "0 12px 12px 0",
        fontFamily: "var(--display)",
        fontSize: 19,
        lineHeight: 1.5,
        color: TEXT,
        fontWeight: 500,
      }}
    >
      {children}
    </blockquote>
  );
}

function InlineCTA({ children, href, label }) {
  return (
    <div
      style={{
        margin: "36px 0",
        padding: "24px 28px",
        borderRadius: 14,
        background: "rgba(79,205,196,0.06)",
        border: "1px solid rgba(79,205,196,0.2)",
      }}
    >
      <div style={{ fontSize: 15, lineHeight: 1.7, color: TEXT_DIM, marginBottom: 14 }}>{children}</div>
      <a
        href={href}
        style={{
          display: "inline-block",
          borderRadius: 10,
          padding: "12px 28px",
          fontSize: 15,
          fontWeight: 600,
          minHeight: 44,
          background: TEAL,
          color: "#000",
          textDecoration: "none",
        }}
      >
        {label}
      </a>
    </div>
  );
}

const th = (accent) => ({
  textAlign: "left",
  padding: "12px 14px",
  color: accent ? TEAL : TEXT,
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  fontFamily: "var(--mono)",
});
const td = (strong) => ({
  padding: "13px 14px",
  color: strong ? TEXT : TEXT_DIM,
  fontWeight: strong ? 600 : 400,
  verticalAlign: "top",
});

function DataTable({ title, note, headers, rows, minWidth = 680 }) {
  return (
    <div style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: 24, margin: "16px 0 32px", overflow: "hidden" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 4, fontFamily: "var(--body)" }}>{title}</div>
      <div style={{ fontSize: 12, color: TEXT_DIM, marginBottom: 16, fontFamily: "var(--body)" }}>{note}</div>
      <div className="blog-scroll-wrap" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", margin: "0 -4px" }}>
        <table style={{ width: "100%", minWidth, borderCollapse: "collapse", fontFamily: "var(--body)", fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.15)" }}>
              {headers.map((h, i) => (
                <th key={h} scope="col" style={th(i === 0)}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row[0]} style={{ borderBottom: i === rows.length - 1 ? "none" : "1px solid rgba(255,255,255,0.06)" }}>
                {row.map((cell, j) => (
                  <td key={j} style={td(j === 0)}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Visual: the B2B exclusion checklist ── */
function ExclusionChecklist() {
  const items = [
    ["Current customers", "Every contact at an account that already pays you, unless the campaign is built to sell them more."],
    ["Open opportunities", "Contacts in active deals. Sales is already talking to them, and an ad adds cost, not news."],
    ["People who already converted", "Anyone who booked a demo or started a trial, so a conversion campaign stops paying to win them twice."],
    ["Your own team", "Employees, and agency or partner staff, so their clicks never reach your reports."],
  ];
  return (
    <div style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: 24, margin: "16px 0 32px" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 4, fontFamily: "var(--body)" }}>Four exclusion lists most B2B teams can build this week</div>
      <div style={{ fontSize: 12, color: TEXT_DIM, marginBottom: 16, fontFamily: "var(--body)" }}>All four come from your own CRM and none needs 25,000 matched users.</div>
      {items.map(([name, note], i) => (
        <div key={name} style={{ display: "flex", gap: 14, padding: "12px 0", borderBottom: i === items.length - 1 ? "none" : "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: TEAL, fontFamily: "var(--mono)", minWidth: 22 }}>{i + 1}</div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: TEXT, fontFamily: "var(--body)" }}>{name}</div>
            <div style={{ fontSize: 13, color: TEXT_DIM, lineHeight: 1.55, fontFamily: "var(--body)" }}>{note}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

const useRows = [
  ["Exclude", "Campaign, Custom audiences", "None. A small or even empty audience works once it is Ready", "Yes, today"],
  ["Include", "Campaign, Custom audiences", "25,000 matched users (OpenAI recommends 100,000)", "Only with a large list"],
  ["Bid adjustment", "Ad group, Advanced (manual bid only)", "25,000 matched users (OpenAI recommends 100,000)", "Only with a large list"],
];

const formatRows = [
  ["Email address", "email", "One @, no spaces. Uppercase is fine; it is lowercased for you."],
  ["Phone number", "phone_number", "E.164 format, with + and the country code."],
  ["Hashed email", "email_sha256", "SHA-256 of the trimmed, lowercased address, as a 64-character hex string."],
  ["Hashed phone", "phone_number_sha256", "SHA-256 of the E.164 number, as a 64-character hex string."],
  ["Google Advertising ID", "gaid", "The raw, hyphenated ID. Do not hash it."],
];

const faqs = [
  {
    q: "Can I use a small customer list for ChatGPT Ads custom audiences?",
    a: "Yes, for exclusion. OpenAI states that audiences below 25,000 matched users can be used for exclusions, and its developer documentation says no minimum matched size is required for that use. A small list shows as Ready with an Exclusion only note. It cannot be used to target people or to adjust bids until it reaches 25,000 matched users.",
  },
  {
    q: "What is the minimum audience size for ChatGPT Ads?",
    a: "Inclusion audiences and audience bid adjustments need at least 25,000 matched users, and OpenAI recommends 100,000. Matched is the word that matters: duplicates, invalid values and identifiers that do not match a ChatGPT user do not count, so uploading 25,000 rows is not enough. Exclusion audiences have no minimum.",
  },
  {
    q: "How do I exclude existing customers from ChatGPT Ads?",
    a: "Export your customers' email addresses or phone numbers from your CRM into a UTF-8 CSV, then in Ads Manager open Tools, Audiences, and Create audience to upload it. When the audience shows Ready, which usually takes 20 to 30 minutes, add it to each campaign under Custom audiences as an exclude audience. Update the list as customers change.",
  },
  {
    q: "What file format does ChatGPT Ads accept for customer lists?",
    a: "A UTF-8 CSV or TXT file up to 500 MB. A CSV needs a header row naming each identifier column: email, phone_number, email_sha256, phone_number_sha256 or gaid, and you can mix types in one file. A TXT file holds one identifier per line, and you choose its identifier type when you upload it.",
  },
  {
    q: "Can I see which people matched my ChatGPT Ads audience?",
    a: "No. OpenAI states that Ads Manager does not show individual matched users or let you select specific people from an audience. You see the audience's status, a matched-user range such as Under 25K, and a count of invalid identifiers. Uploaded files are deleted after processing, usually within 24 hours.",
  },
  {
    q: "Do ChatGPT Ads custom audiences work in Europe?",
    a: "Not in the European Economic Area or Switzerland. OpenAI says custom audiences are not supported for campaigns targeting those regions, because personalized ads are not yet available there. Campaigns for those markets have to rely on context hints and location targeting instead.",
  },
  {
    q: "Why can't my buyers see my ChatGPT ads even if they are on my list?",
    a: "Because of their ChatGPT plan, not your list. OpenAI does not show ads to users on Plus, Pro or any Business plan, or to users under 18. A buyer who uses ChatGPT through their company's Business plan never sees an ad there, whether or not your audience includes them.",
  },
];

const howToSteps = [
  ["Build the list from your CRM", "Export the first-party contacts you want to exclude, such as current customers, with email addresses or phone numbers you have the right to use for ads."],
  ["Format the file", "Save a UTF-8 CSV with a header row (email, phone_number, email_sha256, phone_number_sha256 or gaid) and normalize the values as OpenAI documents."],
  ["Create the audience in Ads Manager", "Open Tools, then Audiences, select Create audience, name it, upload the file, and select Create."],
  ["Wait for Ready", "Processing usually takes 20 to 30 minutes. A list under 25,000 matched users shows as Ready with an Exclusion only note."],
  ["Exclude it in a campaign", "In the campaign's Custom audiences section, add the audience as an exclude audience and save the campaign."],
  ["Keep it current", "Add, remove or replace members as your CRM changes. Campaigns that use the audience pick up the change without being edited."],
];

function Content() {
  return (
    <article>
      {/* ── Overview box ── */}
      <div
        style={{
          margin: "0 0 32px",
          padding: "20px 24px",
          borderRadius: 12,
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.07)",
        }}
      >
        <P>
          <Strong>
            To set up a ChatGPT Ads custom audience, upload a CSV of your customers&#39; emails or phone numbers in Ads Manager under Tools and Audiences, wait for it to show Ready, then add it to a campaign. For B2B, start with exclusion: it is the one use with no minimum list size.
          </Strong>
        </P>
        <ul style={{ paddingLeft: 20, margin: 0 }}>
          <Li><Strong>Exclusion, any size:</Strong> lists under 25,000 matched users still work, marked Exclusion only</Li>
          <Li><Strong>Targeting and bid adjustments:</Strong> 25,000 matched users, and OpenAI recommends 100,000</Li>
          <Li><Strong>Who never sees ads:</Strong> Plus, Pro and Business plan users, so many work accounts are out of reach</Li>
          <Li><Strong>Not in Europe:</Strong> unsupported for campaigns targeting the EEA or Switzerland</Li>
          <Li><Strong>Your data:</Strong> first-party lists only, never purchased or broker data</Li>
        </ul>
      </div>

      <H2 id="what-they-do">What custom audiences do, and who they can never reach</H2>
      <P>
        <Strong>A <A href="/glossary/custom-audiences">custom audience</A> is a list of your own customers or prospects that <A href="/glossary/chatgpt-ads">ChatGPT Ads</A> matches against its users.</Strong> You upload email addresses, phone numbers or advertising IDs, and the platform finds the matching ChatGPT accounts. Ads Manager never shows you who matched.
      </P>
      <P>
        You can use a matched audience in three ways, and they do not share the same rules:
      </P>
      <DataTable
        title="Three ways to use a custom audience"
        note="Per OpenAI's Help Center and developer documentation, October 17, 2026."
        headers={["Use", "Where you set it", "Size requirement", "Useful to most B2B teams?"]}
        rows={useRows}
        minWidth={700}
      />
      <P>
        One limit applies before any list does. OpenAI&#39;s <A href="https://help.openai.com/en/articles/20001207-ads-in-chatgpt-the-basics">overview of ads in ChatGPT</A> states that it does not show ads to users on Plus, Pro or any Business plan, or to users under 18. A buyer who uses ChatGPT through their company&#39;s Business plan never sees an ad there, whatever your audience says. The people a list can reach are the ones using ChatGPT on plans that carry ads.
      </P>
      <PullQuote>
        For targeting, a custom audience needs 25,000 matched users. For exclusion, it needs none. That difference decides whether a B2B team can use the feature at all.
      </PullQuote>

      <H2 id="start-with-exclusions">Why B2B should start with exclusions</H2>
      <P>
        <Strong>Exclusion is the only use with no size floor, so it is the only one most B2B lists qualify for.</Strong> OpenAI&#39;s <A href="https://help.openai.com/en/articles/20001346-set-up-custom-audiences-for-your-campaign">custom audiences guide</A> says audiences below 25,000 matched users can be used for exclusions, and its developer documentation adds that no minimum matched size is required for that use.
      </P>
      <P>
        That is worth saying plainly, because several widely cited guides still say otherwise, describing the minimum as applying to exclusions too or treating a small suppression list as too small to use. OpenAI&#39;s current documentation is clear, and Ads Manager says it in plain words. When we created an empty test audience on October 17, 2026, it reached <Strong>Ready</Strong> with an <Strong>Exclusion only</Strong> label and the note &ldquo;Available for campaign exclusions; unavailable for inclusion targeting and bid adjustments.&rdquo;
      </P>
      <ScreenshotFrame
        src="/screenshots/chatgpt-ads-manager/chatgpt-ads-manager-custom-audience-ready-exclusion-only.png"
        title="Ads Manager / Audiences"
        alt="ChatGPT Ads Manager audience list showing a test audience marked Ready and Exclusion only, available for campaign exclusions, with matched users under 25K."
        caption="Our empty test audience: Ready, Exclusion only, with matched users shown as a range rather than a count."
      />
      <P>
        An exclusion list does not find new buyers. It stops you paying to show ads to people you have already won or are already talking to, which on a B2B budget is often the fastest saving available.
      </P>
      <ExclusionChecklist />
      <P>
        The third list pairs naturally with a <A href="/blog/how-to-set-up-chatgpt-ads-conversion-campaigns">Conversions campaign</A>: once someone has booked a demo, the campaign has done its job for that person. The targeting that finds new buyers is still <A href="/glossary/context-hint">context hints</A>, which describe the conversation rather than the person and have no size floor at all; our guide to <A href="/blog/how-to-write-effective-context-hints">writing effective context hints</A> covers how to build them.
      </P>
      <InlineCTA href={CONVERTER} label="Convert your Google Ads keywords free">
        Custom audiences decide who to leave out. Context hints decide who to reach. The free Google Ads to ChatGPT Ads converter turns the keywords you already run on Google into ChatGPT Ads context hints, with no sign-in needed for a preview.
      </InlineCTA>

      <H2 id="build-the-list">Before you upload: building a list OpenAI will accept</H2>
      <P>
        <Strong>The file is a UTF-8 CSV or TXT of up to 500 MB, holding email addresses, phone numbers, their SHA-256 hashes, or Google Advertising IDs.</Strong> A CSV needs a header row that names each column, and you can mix identifier types in one file.
      </P>
      <DataTable
        title="Identifiers ChatGPT Ads accepts"
        note="CSV header names and formatting rules from OpenAI's documentation."
        headers={["Identifier", "CSV header", "Format"]}
        rows={formatRows}
        minWidth={640}
      />
      <P>
        If you hash the values yourself, normalize first: trim and lowercase emails, put phone numbers in E.164, and send the 64-character hex digest, not Base64. OpenAI&#39;s <A href="https://developers.openai.com/ads/custom-audiences">developer documentation</A> also warns not to remove dots or plus tags from email addresses, a step some list tools do by default.
      </P>
      <P>
        Two B2B cautions. First, ChatGPT matches your list against its own accounts, and a buyer&#39;s ChatGPT account may use a personal address rather than the work email in your CRM. OpenAI does not publish match rates, so treat matching as uncertain and include every identifier you hold lawfully. Second, OpenAI&#39;s rules, which its <A href="/blog/chatgpt-ads-international-expansion-ad-tools-terms">Ad Tools Terms</A> formalized in June, are strict: upload only first-party data you have the right to use for ads, never purchased or broker lists, and get privacy or legal approval for your use case. Campaigns targeting the EEA or Switzerland cannot use custom audiences at all.
      </P>
      <PullQuote>
        If your buyers use ChatGPT through their company&#39;s Business plan, no list will put your ad in front of them there. Plan for the accounts that can see ads.
      </PullQuote>

      <H2 id="create-the-audience">How to create the audience in Ads Manager, step by step</H2>
      <P>
        <Strong>Audiences live in their own section of <A href="/blog/how-to-use-chatgpt-ads-manager">Ads Manager</A>, separate from campaigns, so you build them once and reuse them.</Strong> These steps follow OpenAI&#39;s guide and Ads Manager as of October 17, 2026.
      </P>
      <H3>1. Open Tools, then Audiences</H3>
      <P>
        Expand <Strong>Tools</Strong> in the left sidebar and select <Strong>Audiences</Strong>. Any audiences you have already built are listed here with their status, a matched-users range and a count of invalid identifiers. With none yet, the page invites you to upload raw or hashed emails and phone numbers.
      </P>
      <ScreenshotFrame
        src="/screenshots/chatgpt-ads-manager/chatgpt-ads-manager-audiences-page-create-audience.png"
        title="Ads Manager / Tools / Audiences"
        alt="ChatGPT Ads Manager Audiences page under Tools, empty, with a Create audience button and a prompt to upload raw or hashed emails and phone numbers."
        caption="Tools, then Audiences. Audiences are built here once and reused across campaigns."
      />
      <H3>2. Create the audience and upload the file</H3>
      <P>
        Select <Strong>Create audience</Strong> and give it a name you will recognize in a campaign later, such as &ldquo;Exclude: current customers&rdquo;. A description is optional, and so is the file: Ads Manager lets you create an empty audience and add members later. For a CSV, Ads Manager reads the identifier types from the header; for a TXT file, you choose the type. Then select <Strong>Create audience</Strong>.
      </P>
      <ScreenshotFrame
        src="/screenshots/chatgpt-ads-manager/chatgpt-ads-manager-create-audience-dialog.png"
        title="Ads Manager / Create audience"
        alt="ChatGPT Ads Manager Create audience dialog with fields for audience name, an optional description, and an optional CSV or TXT audience file up to 500 MB."
        caption="The Create audience dialog. The file is optional, so you can set up the audience first and fill it later."
      />
      <H3>3. Wait for Ready</H3>
      <P>
        OpenAI says processing usually takes about 20 to 30 minutes, depending on file size; our empty test audience went from Publishing to Ready in under a minute. You may see interim statuses such as Upload pending, Indexing or Publishing; the audience is usable only once it says <Strong>Ready</Strong>. A <Strong>Failed</Strong> status usually means the file is not valid UTF-8, uses an unsupported identifier, or breaks the formatting rules above.
      </P>
      <P>
        Do not be surprised if the matched count is lower than your row count. Duplicates, invalid values and identifiers that match no ChatGPT user all drop out, which is why OpenAI calls 25,000 a planning threshold and warns that uploading 25,000 identifiers does not guarantee 25,000 matches.
      </P>

      <H2 id="add-to-a-campaign">How to exclude (or include) the audience in a campaign</H2>
      <P>
        <Strong>Audiences attach at the campaign level, in a Custom audiences section with one control for including and one for excluding.</Strong> With no audiences in the account, the buttons read Create include audience and Create exclude audience; once you have one, they read Include audience and Exclude audience, as in OpenAI&#39;s Help Center.
      </P>
      <ScreenshotFrame
        src="/screenshots/chatgpt-ads-manager/chatgpt-ads-manager-campaign-custom-audiences-include-exclude.png"
        title="Ads Manager / Custom audiences"
        alt="ChatGPT Ads Manager campaign Custom audiences section with the Excluded audiences picker open and an exclusion-only test audience available to select."
        caption="Excluded audiences accepts a small audience. Include limits delivery to an audience; exclude filters it out."
      />
      <P>
        Select <Strong>Exclude audience</Strong>, then choose your ready audience from the Excluded audiences list. Three rules from OpenAI apply when you combine them:
      </P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>One role per audience.</Strong> The same audience cannot be both included and excluded in one campaign.</Li>
        <Li><Strong>Exclusions shrink inclusions.</Strong> If you include one audience and exclude another, what remains must still reach 25,000 matched users.</Li>
        <Li><Strong>Several lists stack.</Strong> Exclude more than one audience and anyone in any of them is left out.</Li>
      </ul>
      <P>
        A small audience still appears in the Included audiences list, but greyed out. Hover over it and Ads Manager explains: &ldquo;Audience does not meet the requirements for inclusion targeting.&rdquo;
      </P>
      <ScreenshotFrame
        src="/screenshots/chatgpt-ads-manager/chatgpt-ads-manager-include-audience-unavailable-under-25k.png"
        title="Ads Manager / Included audiences"
        alt="ChatGPT Ads Manager Included audiences picker with an under-25K test audience greyed out and a tooltip saying it does not meet the requirements for inclusion targeting."
        caption="The same audience under Included audiences: listed as under 25K and unavailable."
      />
      <P>
        Exclusion works at the campaign level, so add your lists to every campaign they apply to. A customer list belongs on acquisition campaigns, not on a campaign built to sell more to existing accounts.
      </P>

      <H2 id="bid-adjustments">Audience bid adjustments: bidding more for people you know</H2>
      <P>
        <Strong>A bid adjustment raises or lowers an ad group&#39;s maximum bid when the viewer belongs to an audience you choose, from 0.1x to 10x.</Strong> It does not decide who can see the campaign; it only changes how hard the ad group bids for them.
      </P>
      <ScreenshotFrame
        src="/screenshots/chatgpt-ads-manager/chatgpt-ads-manager-ad-group-audience-bid-adjustments-empty.png"
        title="Ads Manager / Audience bid adjustments"
        alt="ChatGPT Ads Manager ad group with Maximum CPC selected and Audience bid adjustments under Advanced saying no custom audiences are available."
        caption="Bid adjustments sit under Advanced and need a manual bid. This panel showed no audiences even though our exclusion-only test audience existed."
      />
      <P>
        Three details are easy to miss. The Audience bid adjustments section appears only with a manual bid, labelled <Strong>Maximum CPC</Strong> in a Clicks campaign and <Strong>Manual: Max bid</Strong> in a Conversions campaign; with Autobid it disappears. Exclusion-only audiences are not offered here at all: with one in the account, the panel still said &ldquo;No custom audiences available&rdquo;, so do not read that message as a missing audience. And when a viewer is in more than one adjusted audience, the highest multiplier applies.
      </P>
      <P>
        Bid adjustments carry the same 25,000 matched-user floor as inclusion, so most B2B teams will not have them yet. If you do, manual bidding has its own case for B2B, which our post on <A href="/blog/chatgpt-ads-maximize-results-b2b">automatic bidding for B2B</A> sets out.
      </P>

      <H2 id="keep-lists-current">Keeping your lists current</H2>
      <P>
        <Strong>An exclusion list is only as good as its last update.</Strong> A customer who signed last week and is not on the list will keep seeing your acquisition ads.
      </P>
      <P>
        Each audience&#39;s Actions menu in Ads Manager offers Add members, Remove members, Replace members, Merge audiences and Archive. Changing members does not mean recreating the audience or editing the campaigns and ad groups that use it, so the campaign keeps the same audience while the list behind it changes. A monthly export from your CRM and a Replace upload keeps the main lists honest, and teams with a developer can automate the same steps through the Ads API, which merges 2 to 64 audiences into a new one. Retired audiences are archived; the menu has no delete.
      </P>
      <P>
        If you are weighing whether your list will ever be large enough to target with, our breakdown of the <A href={MINIMUM}>25,000-user minimum</A> compares it with Google, Meta and LinkedIn and covers what to do while you grow toward it.
      </P>

      <H2 id="mistakes">Mistakes to avoid</H2>
      <P>
        <Strong>Most mistakes here waste the list rather than the budget, but a stale or wrongly formatted list quietly does both.</Strong>
      </P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>Skipping custom audiences because of the 25,000 floor.</Strong> The floor applies to targeting and bid adjustments, not exclusion.</Li>
        <Li><Strong>Uploading purchased or broker lists.</Strong> OpenAI&#39;s rules allow first-party data only.</Li>
        <Li><Strong>Hashing badly.</Strong> Unnormalized values, Base64 instead of hex, or stripped email dots and plus tags all lose matches.</Li>
        <Li><Strong>Excluding until nothing is left.</Strong> With an include audience, the remainder after exclusions must still reach 25,000 matched users.</Li>
        <Li><Strong>Using custom audiences on EEA or Swiss campaigns.</Strong> They are not supported there.</Li>
        <Li><Strong>Never updating the list.</Strong> New customers and closed deals keep seeing ads meant for strangers.</Li>
      </ul>
      <P>
        For the rest of the campaign build, the <A href="/blog/how-to-set-up-chatgpt-ad-campaign">full campaign setup walkthrough</A> covers creative, bids and review, and the <A href="/blog/how-chatgpt-ads-work#custom-audiences">overview of how ChatGPT Ads work</A> places audiences alongside context hints and the pixel.
      </P>

      {/* ── HowTo + FAQPage JSON-LD ── */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How to set up a ChatGPT Ads custom audience for exclusion",
            step: howToSteps.map(([name, text], i) => ({
              "@type": "HowToStep",
              position: i + 1,
              name,
              text,
            })),
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          }),
        }}
      />

      <H2 id="faq">Frequently Asked Questions</H2>
      <FAQItems faqs={faqs} />

      <H2 id="related">Related Reading</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><A href={MINIMUM}>ChatGPT Ads Custom Audiences Need 25,000 Users: The B2B Reality</A></Li>
        <Li><A href="/blog/how-to-set-up-chatgpt-ads-conversion-campaigns">How to Set Up a ChatGPT Ads Conversion Campaign for B2B</A></Li>
        <Li><A href="/blog/what-are-context-hints-in-chatgpt-ads">What Are Context Hints in ChatGPT Ads? The 5 Patterns Defined</A></Li>
        <Li><A href="/blog/chatgpt-search-ads-b2b">ChatGPT Search Ads for B2B: Early Results, What&#39;s Working, and What Isn&#39;t</A></Li>
        <Li><A href="/blog/how-to-set-up-chatgpt-ad-campaign">How to Set Up a ChatGPT Ad Campaign in 8 Steps (September 2026)</A></Li>
      </ul>
    </article>
  );
}

const post = {
  slug: "how-to-set-up-chatgpt-ads-custom-audiences",
  title: "How to Set Up ChatGPT Ads Custom Audiences for B2B (Start With Exclusions)",
  seoTitle: "How to Set Up ChatGPT Ads Custom Audiences (B2B)",
  excerpt:
    "Most B2B lists will never reach the 25,000 matched users ChatGPT Ads needs for targeting, but exclusion has no minimum. How to build, upload and use custom audiences, starting with the lists you can use today.",
  metaDescription:
    "How to set up ChatGPT Ads custom audiences for B2B: upload a first-party list, wait for Ready, and start with exclusions, the one use with no minimum size.",
  date: "2026-10-19",
  readTime: "10 min read",
  category: "AI Ads",
  tags: ["ChatGPT Ads", "Custom Audiences", "Audience Exclusion", "Customer Lists", "B2B ChatGPT Ads", "HowTo"],
  fiveA: "ads",
  Content,
};

export default post;
